import {
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  Button,
} from "@chakra-ui/react";
import { useLoading } from "../hooks/form";
import { useCallback, useEffect, useState } from "react";
import actions from "../../actions";
import { KYDBatchPayout, KYDBook } from "@common/interfaces/KYDOrganization";
import { useDialog, useDialogWithOptions } from "../../Common/Dialog";
import PayoutsTable from "./PayoutsTable";
import { FiFilter } from "react-icons/fi";
import FilterDialog, {
  FilterDialogContext,
  withFilterDialog,
} from "./FilterDialog";
import { PaymentReportPeriod } from "@common/interfaces/KYDMetrics";
import dayjs from "dayjs";
import { Card } from "../Card";

type OrganizationPayables = Pick<
  KYDBook,
  "available_payout" | "available_payout_display" | "hold" | "hold_display"
>;

const payableCardValue = ({
  isLoading,
  hasError,
  display,
}: {
  isLoading: boolean;
  hasError: boolean;
  display?: string;
}) => {
  if (isLoading) {
    return "—";
  }
  if (hasError || display == null) {
    return "Unavailable";
  }
  return display;
};

const PayoutsDashboard = () => {
  const breakpointValue = useBreakpointValue({ base: "xs", lg: "sm" });
  const [loading, updateLoading] = useLoading({
    init: true,
    payables: true,
  });
  const filterDialog = useDialogWithOptions(FilterDialogContext);
  const [payouts, setPayouts] = useState<KYDBatchPayout[]>([]);
  const [payables, setPayables] = useState<OrganizationPayables | null>(null);
  const [payablesError, setPayablesError] = useState(false);
  const dialog = useDialog();
  const [period, setPeriod] = useState<PaymentReportPeriod>("all");
  const [range, setRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: dayjs().subtract(1, "month").toDate(),
    endDate: new Date(),
  });
  const fetchPayables = useCallback(async () => {
    updateLoading("payables", true);
    setPayablesError(false);
    try {
      const result = await actions.ADMIN.fetchOrganizationPayables();
      setPayables(result);
    } catch (err) {
      setPayables(null);
      setPayablesError(true);
      dialog({ text: err.message });
    }
    updateLoading("payables", false);
  }, [dialog, updateLoading]);
  const fetchPayouts = useCallback(
    async (period: PaymentReportPeriod) => {
      updateLoading("init", true);
      try {
        const _ = await actions.ADMIN.fetchOrganizationPayouts({
          period,
          start_at: dayjs(range.startDate).toISOString(),
          end_at: dayjs(range.endDate).toISOString(),
        });
        setPayouts(_);
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
    },
    [updateLoading, dialog, range]
  );

  useEffect(() => {
    fetchPayables();
  }, [fetchPayables]);

  useEffect(() => {
    fetchPayouts(period);
  }, [fetchPayouts, period]);

  return (
    <Stack maxW={"5xl"} mx="auto" spacing={{ base: "4", lg: "6" }}>
      <FilterDialog
        initialPreset={period}
        initialStartDate={range.startDate}
        initialEndDate={range.endDate}
        isLoading={loading.init}
        onSave={({ preset, startDate, endDate }) => {
          setPeriod(preset as PaymentReportPeriod);
          setRange({
            startDate: dayjs(startDate).toDate(),
            endDate: dayjs(endDate).toDate(),
          });
          filterDialog.close();
        }}
        onClose={() => {
          filterDialog.close();
        }}
      />
      <VStack h="full" w="full" spacing="4">
        <HStack spacing="1" width={"full"} justifyContent={"space-between"}>
          <Heading size={breakpointValue} fontWeight="medium">
            Payouts
          </Heading>

          <Button
            variant="ghost"
            py={4}
            px={4}
            gap={"8px"}
            onClick={() => filterDialog.open()}
          >
            <FiFilter />
            Filters
          </Button>
        </HStack>
        <Stack direction={{ base: "column", md: "row" }} spacing={4} w="full">
          <Card
            width="100%"
            label="Payout Balance"
            headerSize="md"
            value={payableCardValue({
              isLoading: loading.payables,
              hasError: payablesError,
              display: payables?.available_payout_display,
            })}
          />
          <Card
            width="100%"
            label="Hold Balance"
            headerSize="md"
            value={payableCardValue({
              isLoading: loading.payables,
              hasError: payablesError,
              display: payables?.hold_display,
            })}
          />
        </Stack>
        {payablesError ? (
          <HStack w="full" justifyContent="flex-start">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPayables}
              isLoading={loading.payables}
            >
              Retry balances
            </Button>
          </HStack>
        ) : null}
        <Stack h="full" w="full" flexGrow={1} spacing={{ base: "5", lg: "6" }}>
          {loading["init"] ? (
            <VStack>
              <Spinner />
              <Text>Loading payouts...</Text>
            </VStack>
          ) : (
            <PayoutsTable payouts={payouts} />
          )}
        </Stack>
      </VStack>
    </Stack>
  );
};

export default withFilterDialog(PayoutsDashboard);
